import { useState, type FormEvent } from 'react';
import { entryInput } from '@redirector/shared';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Dialog } from '../ui/Dialog.js';
import { Field } from '../ui/Field.js';
import { issuesToFieldErrors, type FieldErrors } from '../ui/formErrors.js';
import { useGroups } from '../groups/queries.js';
import { parseFullUrl, sameHost } from './parseUrl.js';
import { useCreateUrl, useDomainsAcrossGroups, useUrls, type UrlRow } from './queries.js';

export type AddUrlDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Preselects the group when the dialog is opened from inside one. */
  defaultGroupId?: number;
  /** Receives the new entry id, so a caller can go straight to the URL page. */
  onCreated?: (entryId: number) => void;
};

const MONO = 'font-mono text-[length:var(--text-small)]';

/**
 * Turns a rejected create into a message on the field the operator typed.
 *
 * The server names its conflicts on `path` (the entry) or `hostname` (the
 * domain), but this form has one URL box covering both, so both land there.
 */
function submitErrors(error: unknown): FieldErrors {
  const fields =
    error !== null && typeof error === 'object' && 'fields' in error
      ? (error as { fields: FieldErrors }).fields
      : {};
  const onUrl = fields.path ?? fields.hostname;
  if (onUrl !== undefined) return { url: onUrl };
  if (fields.label !== undefined) return fields;
  return { root: error instanceof Error ? error.message : 'Could not add the URL' };
}

export function AddUrlDialog({ open, onClose, defaultGroupId, onCreated }: AddUrlDialogProps) {
  return (
    <Dialog open={open} title="Add URL" onClose={onClose}>
      {/* Mounted only while open, so a cancelled attempt does not come back
          with the previous URL still in the box. */}
      {open && (
        <AddUrlForm
          onCancel={onClose}
          {...(defaultGroupId === undefined ? {} : { defaultGroupId })}
          {...(onCreated === undefined ? {} : { onCreated })}
          onDone={onClose}
        />
      )}
    </Dialog>
  );
}

type AddUrlFormProps = {
  defaultGroupId?: number;
  onCreated?: (entryId: number) => void;
  onDone: () => void;
  onCancel: () => void;
};

function AddUrlForm({ defaultGroupId, onCreated, onDone, onCancel }: AddUrlFormProps) {
  const groups = useGroups();
  const domains = useDomainsAcrossGroups();
  /* Unfiltered on purpose: a duplicate must be caught even when it lives in a
     group the screen behind this dialog is currently filtered away from. */
  const urls = useUrls();
  const create = useCreateUrl();

  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [groupId, setGroupId] = useState(defaultGroupId === undefined ? '' : String(defaultGroupId));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const parsed = url.trim() === '' ? null : parseFullUrl(url);
  const parts = parsed?.ok === true ? parsed.parts : null;

  /* The whole point of ignoring the scheme: an http URL and the https URL
     with the same host find the same domain here. */
  const existing =
    parts === null
      ? undefined
      : domains.data?.domains.find((domain) => sameHost(domain.hostname, parts.hostname));

  const collision: UrlRow | undefined =
    parts === null || existing === undefined
      ? undefined
      : urls.data?.urls.find(
          (row) => row.domainId === existing.domainId && row.pathNorm === parts.pathNorm,
        );

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (parsed === null || !parsed.ok) {
      setErrors({ url: parsed === null ? 'A URL is required' : parsed.message });
      return;
    }

    if (collision !== undefined) {
      setErrors({
        url: `${collision.fullUrl} is already configured in ${collision.groupName}. Open that URL instead, or choose a different path.`,
      });
      return;
    }

    if (existing === undefined && groupId === '') {
      setErrors({ group: 'Choose the group this new domain belongs to' });
      return;
    }

    const entry = entryInput.safeParse({ label, path: parsed.parts.path, enabled: true });
    if (!entry.success) {
      const issues = issuesToFieldErrors(entry.error.issues);
      setErrors(issues.path === undefined ? issues : { ...issues, url: issues.path });
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      const result = await create.mutateAsync({
        hostname: parsed.parts.hostname,
        entry: entry.data,
        ...(existing === undefined
          ? { groupId: Number(groupId) }
          : { domainId: existing.domainId }),
      });
      onCreated?.(result.entry.id);
      onDone();
    } catch (error) {
      setErrors(submitErrors(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}

      <Field
        label="Full URL"
        error={errors.url}
        hint="Paste the URL clients will link to, for example timpromos.com.br/BTW/summer. The scheme is ignored — http and https are the same URL. No query string; incoming parameters are forwarded automatically."
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            autoFocus
            className={MONO}
            spellCheck={false}
            autoCapitalize="none"
            inputMode="url"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        )}
      </Field>

      {parts !== null && (
        <p className="text-[length:var(--text-small)] text-ink-subtle">
          Matches{' '}
          <span className="font-mono text-ink-muted">
            {parts.hostname}
            {parts.pathNorm}
          </span>{' '}
          — matching ignores the scheme, capitalisation and a trailing slash.
        </p>
      )}

      <Field label="Label" error={errors.label} hint="Optional. What this campaign is, in human terms.">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        )}
      </Field>

      {existing !== undefined ? (
        /* Read-only rather than absent: the group is a fact about this URL, and
           hiding it would leave the operator unsure which client they just
           added a URL for. It cannot be contradicted, because the hostname
           already belongs to a group. */
        <div className="flex flex-col gap-(--space-1)">
          <p className="text-[length:var(--text-small)] font-medium text-ink">Group</p>
          <p className="text-ink">{existing.groupName}</p>
          <p className="text-[length:var(--text-small)] text-ink-subtle">
            <span className="font-mono">{existing.hostname}</span> is already configured, so this
            URL joins its group.
          </p>
        </div>
      ) : (
        <Field
          label="Group"
          error={errors.group}
          hint="This hostname is new, so it will be created under the group you choose."
        >
          {({ id, describedBy, invalid }) => (
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className="w-full"
                disabled={groups.data === undefined}
              >
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {(groups.data?.groups ?? []).map((group) => (
                  <SelectItem key={group.id} value={String(group.id)}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      )}

      <div className="flex justify-end gap-(--space-2)">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Adding…' : 'Add URL'}
        </Button>
      </div>
    </form>
  );
}
